'use client';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { zodResolver } from '@hookform/resolvers/zod';
import DOMPurify from 'dompurify';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import ApiRoutes from '@/constants/ApiRoutes';
import { trackEvent } from '@/utils/umami';

export const ContactSchema = z.object({
    name: z.string().min(2).max(100, 'Name must not exceed 100 characters'),
    email: z
        .string({ required_error: 'Email is required' })
        .email({ message: 'Invalid email address' }),
    subject: z.string().min(3).max(200, 'Subject must not exceed 200 characters'),
    message: z.string().min(10).max(2000, 'Message must not exceed 2000 characters'),
});
type ContactFormValues = z.infer<typeof ContactSchema>;

interface IContractForm {
    captchaSiteKey: string;
}

export default function ContactForm(props: IContractForm) {
    const { captchaSiteKey } = props;
    const [submitStatus, setSubmitStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const form = useForm<ContactFormValues>({
        resolver: zodResolver(ContactSchema),
        defaultValues: {
            name: '',
            email: '',
            subject: '',
            message: '',
        },
    });

    const onSubmit: SubmitHandler<ContactFormValues> = data => {
        trackEvent('contact-submit');
        if (!captchaToken) {
            setSubmitStatus('error');
            trackEvent('contact-error', { reason: 'captcha-missing' });
            // Stop submission if hCaptcha is not completed
            return;
        }

        const sanitizedData = {
            name: DOMPurify.sanitize(data.name),
            email: DOMPurify.sanitize(data.email),
            subject: DOMPurify.sanitize(data.subject),
            message: DOMPurify.sanitize(data.message),
            captchaToken,
        };

        setIsSubmitting(true);
        fetch(`${ApiRoutes.CONTACT}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(sanitizedData),
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Failed to send message');
                }
                setIsSubmitting(false);
                setSubmitStatus('success');
                trackEvent('contact-success');
                form.reset();
                setCaptchaToken(null);
            })
            .catch(() => {
                setIsSubmitting(false);
                setSubmitStatus('error');
                trackEvent('contact-error', { reason: 'request-failed' });
                setCaptchaToken(null);
            });
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='flex w-full max-w-xl flex-col items-center justify-center space-y-4 px-10'>
                <Alerts submitStatus={submitStatus} />
                <FormField
                    name='name'
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className='w-full'>
                            <FormLabel>Name *</FormLabel>
                            <FormControl>
                                <Input placeholder='Name' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name='email'
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className='w-full'>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                                <Input type='email' placeholder='Email' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name='subject'
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className='w-full'>
                            <FormLabel>Subject *</FormLabel>
                            <FormControl>
                                <Input type='text' placeholder='Subject' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    name='message'
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className='w-full'>
                            <FormLabel>Message *</FormLabel>
                            <FormControl>
                                <Textarea
                                    className='max-h-[300px] min-h-[200px]'
                                    placeholder='Your message'
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <HCaptcha
                    sitekey={captchaSiteKey}
                    onVerify={token => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                />

                <Button disabled={isSubmitting || !captchaToken} className='w-full'>
                    Send
                </Button>
            </form>
        </Form>
    );
}

const fadeInVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
};

const Alerts = ({ submitStatus }: { submitStatus: 'success' | 'error' | null }) => {
    return (
        <AnimatePresence mode='wait'>
            {submitStatus === 'success' && (
                <motion.div
                    key='success-alert'
                    variants={fadeInVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    className='w-full'
                    transition={{ duration: 0.3 }}>
                    <Alert variant='success'>
                        <AlertTitle className='text-lg font-bold'>Success</AlertTitle>
                        <AlertDescription className='text-base font-semibold'>
                            Your message was sent successfully!
                        </AlertDescription>
                    </Alert>
                </motion.div>
            )}

            {submitStatus === 'error' && (
                <motion.div
                    key='error-alert'
                    variants={fadeInVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    className='mb-8 w-full'
                    transition={{ duration: 0.3 }}>
                    <Alert variant='destructive'>
                        <AlertTitle className='text-lg font-bold'>Error</AlertTitle>
                        <AlertDescription className='text-base font-semibold'>
                            Something went wrong, please try again.
                        </AlertDescription>
                    </Alert>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
